/**
 * Reported-Failure Builder
 *
 * @module @chatsdet/diagnostic-workspace/reported-failure-builder
 * @description Writes `reported-failure.md` — the test runner's hypothesis about what
 * failed. Treated as a hypothesis to verify, not a fact, by the diagnostic agent's
 * procedural prompt. Always includes the explicit "verify before trusting" header.
 */

import * as fs from 'node:fs/promises';
import { resolveWorkspacePath, WORKSPACE_PATHS } from './paths.js';

export interface ReportedFailureInput {
  workspaceRoot: string;
  runStatus: string;
  failedStepName: string | null;
  failedStepOrderIndex: number | null;
  errorMessage: string | null;
}

export const writeReportedFailure = async (input: ReportedFailureInput): Promise<void> => {
  const lines: string[] = [];
  lines.push('# Reported Failure');
  lines.push('');
  lines.push(
    '**This is the test runner\'s hypothesis. Verify with evidence before trusting it — the root cause is often upstream of the visible failure.**',
  );
  lines.push('');
  lines.push(`Run status: \`${input.runStatus}\``);
  lines.push('');

  if (input.runStatus !== 'failed') {
    lines.push(
      `> The run did not end in a \`failed\` status. The test runner did not designate a failed step.`,
    );
    lines.push('');
  } else if (!input.failedStepName) {
    lines.push(
      `> The run is \`failed\` but no failed step was identified. Investigate the run-level error message and walk the timeline.`,
    );
    lines.push('');
  } else {
    const ordinal = input.failedStepOrderIndex !== null ? ` (step ${input.failedStepOrderIndex})` : '';
    lines.push(`Failed step${ordinal}: **${input.failedStepName}**`);
    lines.push('');
  }

  if (input.errorMessage) {
    lines.push('## Error message');
    lines.push('');
    lines.push('```');
    lines.push(input.errorMessage);
    lines.push('```');
    lines.push('');
  }

  lines.push('## What to do next');
  lines.push('');
  lines.push(
    '1. Read `summary.md` for the run-level overview and step table.',
  );
  lines.push(
    '2. Open the failed step\'s rollup at `steps/<NNN>-<stepId>.md` to gather evidence at the failure point.',
  );
  lines.push(
    '3. Walk backwards through prior `steps/*.md` rollups — the divergence often happens upstream.',
  );
  lines.push('');

  await fs.writeFile(
    resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.reportedFailure),
    `${lines.join('\n')}\n`,
    'utf-8',
  );
};
